import { fetchEventSource } from '@microsoft/fetch-event-source';

import AuthenticationStore from '~/auth/authenticationStore';
import { refreshAccessToken } from '~/auth/refreshAccessToken';
import { LOGIN_CONSTANTS } from '~/common/constants/login';
import { camelizeKeys } from '~/redux/api/transformers/utils';
import { callerTimezoneHeader } from '~/utils/callerTimezone';

const authStore = AuthenticationStore();

class RateLimitError extends Error {}
class AuthenticationError extends Error {}

export interface SseConnectionCallbacks {
  /**
   * Receives the parsed (camelized) event data. `eventName` is the SSE named
   * event (the `event:` line), forwarded for servers that key on it (e.g.
   * hermes `thinking`/`token`/`complete`). Consumers that encode their type in
   * the data body can ignore it.
   */
  onMessage: (event: unknown, eventName?: string) => void;
  onOpen?: (res: Response) => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;
  onAuthenticationError?: (err: unknown) => void;
  onRateLimitError?: () => void;
  onTimeout?: () => void;
}

export interface SseConnectionOptions {
  method: 'POST' | 'GET';
  headers?: Record<string, string>;
  /** Serialized request body, forwarded to fetchEventSource (e.g. POST JSON). */
  body?: string;
  openWhenHidden?: boolean;
  timeout?: number;
  /**
   * Top-level payload keys whose value must reach `onMessage` untouched.
   * Camelization recurses, so a nested server document such as a generated
   * workflow specification would otherwise have its own `snake_case` keys
   * rewritten and stop matching what the server expects back.
   */
  keysToExcludeFromCamelization?: string[];
}

/**
 * Creates a standalone SSE connection using fetchEventSource.
 * Unlike the useSse hook, this function has no React dependencies
 * and can be used from RTK Query middleware or other non-React contexts.
 *
 * Returns an AbortController that can be used to close the connection.
 */
export const createSseConnection = (
  url: string,
  callbacks: SseConnectionCallbacks,
  options: SseConnectionOptions = { method: 'GET' },
): AbortController => {
  const abortController = new AbortController();

  const connect = (hasRetried = false) => {
    setupConnection(url, callbacks, options, abortController).catch(
      async (err) => {
        if (abortController.signal.aborted) return;
        if (hasRetried) {
          callbacks.onAuthenticationError?.(err);
        } else if (err instanceof AuthenticationError) {
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            // A failed refresh has already cleared the session, so a retry
            // could only fail with another 401. Nothing else would tell the
            // caller, because the inactivity timer only starts after a
            // connection opens.
            callbacks.onAuthenticationError?.(err);
            return;
          }
          if (abortController.signal.aborted) return;
          connect(true);
        }
      },
    );
  };

  connect();
  return abortController;
};

const setupConnection = (
  url: string,
  callbacks: SseConnectionCallbacks,
  options: SseConnectionOptions,
  abortController: AbortController,
): Promise<void> => {
  let timeoutId: NodeJS.Timeout | undefined;

  const clearTimeoutTimer = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  const resetTimeoutTimer = () => {
    clearTimeoutTimer();
    if (abortController.signal.aborted) return;
    if (options.timeout && callbacks.onTimeout) {
      timeoutId = setTimeout(() => {
        callbacks.onTimeout?.();
        abortController.abort();
      }, options.timeout);
    }
  };

  abortController.signal.addEventListener('abort', clearTimeoutTimer, {
    once: true,
  });

  const handleOpen = async (res: Response) => {
    switch (res.status) {
      case 200:
        callbacks.onOpen?.(res);
        resetTimeoutTimer();
        break;
      case 429:
        callbacks.onRateLimitError?.();
        throw new RateLimitError();
      case 401:
        throw new AuthenticationError();
      default:
        throw new Error(`SSE connection failed with status ${res.status}`);
    }
  };

  return fetchEventSource(url, {
    credentials: 'include',
    headers: {
      // The AI agents answer "what time is it", "due in 30 days" and "end of my
      // day" from this header, and it is the only place they can learn the zone
      // from: Evisort stores no timezone for a user. It is set here rather than
      // by each caller because this transport bypasses the axios interceptor
      // that attaches it to every other request, so a caller that forgot it got
      // UTC answers with nothing to show anything was missing.
      ...callerTimezoneHeader(),
      ...(options.headers || { Accept: 'text/event-stream' }),
      ...(authStore.getCsrf()
        ? { [LOGIN_CONSTANTS.HEADERS.CSRF_TOKEN]: authStore.getCsrf() }
        : {}),
      ...(authStore.getAccessToken()
        ? { Authorization: `Bearer ${authStore.getAccessToken()}` }
        : {}),
    },
    method: options.method,
    body: options.body,
    signal: abortController.signal,
    openWhenHidden: options.openWhenHidden ?? true,
    onclose: () => {
      clearTimeoutTimer();
      callbacks.onClose?.();
    },
    onerror: (err) => {
      clearTimeoutTimer();
      if (
        (err instanceof DOMException && err.name === 'AbortError') ||
        err instanceof AuthenticationError ||
        err instanceof RateLimitError
      ) {
        throw err;
      } else {
        callbacks.onError?.(err);
        throw err;
      }
    },
    onmessage: (event) => {
      if (event.data === '') {
        resetTimeoutTimer();
        return;
      }
      resetTimeoutTimer();
      try {
        const eventData = JSON.parse(event.data);
        const parsedEventData = camelizeKeys(
          eventData,
          options.keysToExcludeFromCamelization,
        );
        callbacks.onMessage(parsedEventData, event.event);
      } catch {
        throw new Error('Failed to parse SSE event data.');
      }
    },
    onopen: handleOpen,
  });
};
